export interface ThemeConfig {
  id: string;
  name: string;
  description: string;
  colors: {
    background: string;
    foreground: string;
    card: string;
    cardForeground: string;
    popover: string;
    popoverForeground: string;
    primary: string;
    primaryForeground: string;
    secondary: string;
    secondaryForeground: string;
    muted: string;
    mutedForeground: string;
    accent: string;
    accentForeground: string;
    destructive: string;
    destructiveForeground: string;
    border: string;
    input: string;
    ring: string;
    success: string;
    warning: string;
    info: string;
    sidebar: string;
    sidebarForeground: string;
    sidebarPrimary: string;
    sidebarPrimaryForeground: string;
    sidebarAccent: string;
    sidebarAccentForeground: string;
    sidebarBorder: string;
    sidebarRing: string;
  };
  fonts: {
    display: string;
    mono: string;
    body: string;
  };
  effects: {
    glowEnabled: boolean;
    gridEnabled: boolean;
    glowColor: string;
    gridColor: string;
  };
}

const SHARED_FONTS = {
  display: "'Inter', sans-serif",
  mono: "'Inter', monospace",
  body: "'Inter', sans-serif",
};

const SHARED_EFFECTS = {
  glowEnabled: false,
  gridEnabled: false,
  glowColor: "30, 58, 138",
  gridColor: "30, 58, 138",
};

export const themes: ThemeConfig[] = [
  {
    id: "default",
    name: "Default",
    description: "The single InControl theme.",
    colors: {
      background: "0 0% 98%",
      foreground: "0 0% 4%",
      card: "0 0% 100%",
      cardForeground: "0 0% 4%",
      popover: "0 0% 100%",
      popoverForeground: "0 0% 4%",
      primary: "0 0% 4%",
      primaryForeground: "0 0% 100%",
      secondary: "0 0% 96%",
      secondaryForeground: "0 0% 4%",
      muted: "0 0% 96%",
      mutedForeground: "0 0% 4%",
      accent: "0 0% 4%",
      accentForeground: "0 0% 100%",
      destructive: "346 77% 41%",
      destructiveForeground: "0 0% 100%",
      border: "0 0% 90%",
      input: "0 0% 90%",
      ring: "0 0% 4%",
      success: "173 80% 26%",
      warning: "30 84% 36%",
      info: "0 0% 4%",
      sidebar: "0 0% 100%",
      sidebarForeground: "0 0% 4%",
      sidebarPrimary: "0 0% 4%",
      sidebarPrimaryForeground: "0 0% 100%",
      sidebarAccent: "0 0% 96%",
      sidebarAccentForeground: "0 0% 4%",
      sidebarBorder: "0 0% 90%",
      sidebarRing: "0 0% 4%",
    },
    fonts: SHARED_FONTS,
    effects: SHARED_EFFECTS,
  },
];

// Brand palette — the source of truth for the sage/charcoal brand colors on
// the JS side. Hardcoded hex literals for these tones should NOT be duplicated
// in components; import from here for JS/inline-style consumers (e.g. the
// Pipeline stage accents and the "No follow-up" badge) so a palette shift
// updates every consumer at once.
//
// For Tailwind className usage (text-/bg-/border-/ring-brand-sage, etc.) use the
// matching `--color-brand-*` tokens in the `@theme` block of client/src/index.css.
// That block is the CSS-side mirror of this object — the only other place these
// hexes may appear. Keep the two in sync.
export const BRAND = {
  sage: "#7A8B6F",
  sageDeep: "#5F6F57",
  sageLight: "#9CAD8E",
  charcoal: "#0A0A0A",
  nearBlack: "#1A1A1A",
  charcoalGray: "#4A4A4A",
  charcoalDark: "#2E2E2E",
  offWhite: "#FAFAFA",
} as const;

export const THEME_STORAGE_KEY = "app-theme";
export const DEFAULT_THEME_ID = "default";

export function getThemeById(id: string): ThemeConfig {
  return themes.find((t) => t.id === id) || themes[0];
}
